/** How long a submitted expense claim has been waiting for an approver, counted in London calendar days.
 *
 *  Claims carry no submitted-at stamp, so `updatedAt` stands in: a submitted claim is only edited
 *  by its approver moving it on, so for a claim still in `submitted` it is the day it was sent.
 */
import type { ExpenseClaim } from '../../types/accounting';

/** Days a claim may sit before the list calls it out. */
export const WAITING_FLAG_DAYS = 3;

const toLondonDay = (d: Date): string => d.toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
const dayNumber = (day: string): number => {
  const [y, m, d] = day.split('-').map(Number);
  return Date.UTC(y, m - 1, d) / 86_400_000;
};

/** Whole days since submission, or null when the claim is not awaiting approval or has no usable date. */
export function daysWaiting(claim: Pick<ExpenseClaim, 'status' | 'updatedAt' | 'createdAt'>, now: Date = new Date()): number | null {
  if (claim.status !== 'submitted') return null;
  const since = new Date(claim.updatedAt ?? claim.createdAt ?? '');
  if (Number.isNaN(since.getTime())) return null;
  return Math.max(0, dayNumber(toLondonDay(now)) - dayNumber(toLondonDay(since)));
}

/** "Waiting 5 days" once a submitted claim has passed the flag threshold, otherwise null. */
export function waitingCue(claim: Pick<ExpenseClaim, 'status' | 'updatedAt' | 'createdAt'>, now: Date = new Date()): string | null {
  const days = daysWaiting(claim, now);
  return days !== null && days >= WAITING_FLAG_DAYS ? `Waiting ${days} days` : null;
}

/** Submitted claims, longest-waiting first, so an approver clears the oldest before the newest. Ties keep their order. */
export function sortLongestWaitingFirst<T extends Pick<ExpenseClaim, 'status' | 'updatedAt' | 'createdAt'>>(claims: T[], now: Date = new Date()): T[] {
  return claims
    .map((claim, index) => ({ claim, index, days: daysWaiting(claim, now) ?? -1 }))
    .sort((a, b) => b.days - a.days || a.index - b.index)
    .map((entry) => entry.claim);
}
